import {
	NewOrderBuildGridQuantityColumnHeader,
	NewOrderViewPriceEstimateExtendedPriceColumnHeader,
	NewOrderViewPriceEstimateListPriceColumnHeader
} from "components/NewOrders/constants";
import { BuildNewOrderLineItemsHeader, BuildNewOrderLineItemsHeaderWrapper } from "../NewOrders.styles";
import { EstimatesLineItemGrid, PriceEstimateConfigurations } from "./viewPriceEstimates.styles";
import Header from "components/Common/LineItemGrid/Header/Header";
import Modification from "../../Common/LineItemGrid/Modification/Modification";
import { models } from "types/api/viewModels.ts";
import {
	formatFinishDescription,
	getAttributes,
	getSubheaderAttributes
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import { checkForCustomQuotePricing } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { ColorOptionText, ExtendedUnitPrice, VendorText } from "constants/text.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";

interface ViewPriceEstimatesLineItemsProps {
	priceEstimateConfiguration: models["ValidatedOrderConfigurationViewModel"];
	searchValue: string;
}

const ViewPriceEstimatesLineItems = ({ priceEstimateConfiguration, searchValue }: ViewPriceEstimatesLineItemsProps) => {
	const { isCustomColorPage: isCustomColor } = useUserLocation();
	return (
		<>
			<BuildNewOrderLineItemsHeaderWrapper>
				<BuildNewOrderLineItemsHeader>
					<div data-testid="new-order-build-label">
						<div>{getAttributes(priceEstimateConfiguration, isCustomColor)}</div>
						<div>{getSubheaderAttributes(priceEstimateConfiguration)}</div>
					</div>
				</BuildNewOrderLineItemsHeader>
			</BuildNewOrderLineItemsHeaderWrapper>
			<PriceEstimateConfigurations>
				{priceEstimateConfiguration?.lineItems?.map((item) => {
					const foundSearch = `${item.sku} - ${item.description}`
						.toUpperCase()
						.includes(searchValue.toUpperCase());
					if (foundSearch) {
						return (
							<EstimatesLineItemGrid
								key={`${item.sku}-${item.lineItemNumber}`}
								mainRowContent={[
									<Header
										key={`${item.sku}-${item.lineItemNumber}-header`}
										item={item}
									/>,
									isCustomColor ? (
										<div key={`${item.sku}-${item.lineItemNumber}-vendor`}>
											<div>{VendorText}</div>
											<div data-testid="view-price-estimate-line-item-vendor">
												{
													priceEstimateConfiguration.globals?.finish?.customColor
														?.vendorDescription
												}
											</div>
										</div>
									) : (
										<div />
									),
									isCustomColor ? (
										<div key={`${item.sku}-${item.lineItemNumber}-colorOption`}>
											<div>{ColorOptionText}</div>
											<div data-testid="view-price-estimate-line-item-colorOption">
												{isCustomColor
													? `${priceEstimateConfiguration.globals?.finish?.customColor?.colorCode} - ${priceEstimateConfiguration.globals?.finish?.customColor?.colorDescription}`
													: formatFinishDescription(
															priceEstimateConfiguration.globals?.finish
														)}
											</div>
										</div>
									) : (
										<div />
									),
									<div key={`${item.sku}-${item.lineItemNumber}-qty`}>
										<div>{NewOrderBuildGridQuantityColumnHeader}</div>
										<div data-testid="view-price-estimate-line-item-quantity">
											{item.quantityOrdered}
										</div>
									</div>,
									<div key={`${item.sku}-${item.lineItemNumber}-list-price`}>
										<div>{NewOrderViewPriceEstimateListPriceColumnHeader}</div>
										<div data-testid="view-price-estimate-line-item-listPrice">
											{checkForCustomQuotePricing(item, item?.listPrice)}
										</div>
									</div>,
									<div key={`${item.sku}-${item.lineItemNumber}-extended-price`}>
										<div>
											{item?.extendedNetPrice
												? ExtendedUnitPrice
												: NewOrderViewPriceEstimateExtendedPriceColumnHeader}
										</div>
										<div data-testid="view-price-estimate-line-item-extendedPrice">
											{checkForCustomQuotePricing(
												item,
												item?.extendedNetPrice ?? item?.extendedPrice
											)}
										</div>
									</div>
								]}
								modificationRows={item.modifications?.map((modification) => (
									<Modification
										description={modification.description ?? "--"}
										key={modification.sku}
										lineNumber={modification.lineItemNumber}
										title={modification.sku ?? "--"}
										values={modification.values}
									/>
								))}
								dimensionRows={item.requiredDimensions?.map((dimension) => (
									<Modification
										dimensionDescription={dimension.description}
										key={dimension.id}
										dimensionValue={dimension.value}
										isUnrestrictedDimension={dimension.isUnrestrictedValue}
										lineNumber={undefined}
									/>
								))}
								requiredCategoriesRows={item.requiredCategories?.map((category) => (
									<Modification
										key={category.categoryName}
										requiredCategory={category}
										lineNumber={undefined}
									/>
								))}
							/>
						);
					}
					return undefined;
				})}
			</PriceEstimateConfigurations>
		</>
	);
};

export default ViewPriceEstimatesLineItems;
